import { useState, useEffect, useRef } from 'react';
import { MapPin, Link as LinkIcon, Plus, RotateCcw, CheckCircle2, AlertCircle, Loader2, FileText, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ChantierFormDialog } from '@/components/chantiers/ChantierFormDialog';
import ChantierSelector from './ChantierSelector';
import type { MultiOCRFile, MultiOCRResultLine } from '@/types/ocr';

interface MultiOCRResultsProps {
  files: MultiOCRFile[];
  allResultLines: MultiOCRResultLine[];
  isProcessing: boolean;
  currentIndex: number;
  completedCount: number;
  totalCount: number;
  savedCount: number;
  pendingSaveCount: number;
  chantierId: string;
  chantierNom?: string;
  documentType: 'facture' | 'devis' | 'frais';
  restoredFromStore: boolean;
  onLinkToCurrentChantier: (lineId: string) => Promise<void>;
  onLinkToOtherChantier: (lineId: string, chantierId: string, chantierNom: string) => Promise<void>;
  onCreateAndLink: (lineId: string, chantierId: string) => Promise<void>;
  onLinkAllToCurrentChantier: (lineIds?: Set<string>) => Promise<void>;
  onRetry: (lineId: string) => void;
  onDismiss: (lineId: string) => void;
  onCancel: () => void;
  onClose: () => void;
  entrepriseId: string | null;
}

export default function MultiOCRResults({
  files,
  allResultLines,
  isProcessing,
  currentIndex,
  completedCount,
  totalCount,
  savedCount,
  pendingSaveCount,
  chantierId,
  chantierNom,
  documentType,
  restoredFromStore,
  onLinkToCurrentChantier,
  onLinkToOtherChantier,
  onCreateAndLink,
  onLinkAllToCurrentChantier,
  onRetry,
  onDismiss,
  onCancel,
  onClose,
  entrepriseId,
}: MultiOCRResultsProps) {
  const [expandedSelectorLineId, setExpandedSelectorLineId] = useState<string | null>(null);
  const [createChantierLineId, setCreateChantierLineId] = useState<string | null>(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [linkingLineId, setLinkingLineId] = useState<string | null>(null);
  const [batchLinking, setBatchLinking] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  // Ref pour tracker les lignes déjà traitées par le pré-cochage (ne pas écraser les choix manuels)
  const knownLineIdsRef = useRef<Set<string>>(new Set());

  // Pré-cochage intelligent quand de nouvelles lignes passent en 'done'
  useEffect(() => {
    const newIds: string[] = [];
    for (const line of allResultLines) {
      if (line.status !== 'done') continue;
      if (knownLineIdsRef.current.has(line.id)) continue; // Déjà traitée
      knownLineIdsRef.current.add(line.id);

      if (!line.parentFileId) {
        // Facture simple → cochée par défaut
        newIds.push(line.id);
      } else if (chantierNom && line.reference) {
        // Multi-ref → cochée si la ref matche le nom du chantier courant
        const refLower = line.reference.toLowerCase();
        const nomLower = chantierNom.toLowerCase();
        if (nomLower.includes(refLower) || refLower.includes(nomLower)) {
          newIds.push(line.id);
        }
      }
      // Multi-ref sans match → reste décochée
    }
    if (newIds.length > 0) {
      setCheckedIds(prev => {
        const next = new Set(prev);
        for (const id of newIds) next.add(id);
        return next;
      });
    }
  }, [allResultLines, chantierNom]);

  const toggleCheck = (lineId: string) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const checkedPendingCount = allResultLines.filter(
    l => l.status === 'done' && checkedIds.has(l.id)
  ).length;

  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const allDone = allResultLines.length > 0 && allResultLines.every(l => l.status === 'saved');
  const docLabel = documentType === 'facture' ? 'facture' : documentType === 'devis' ? 'devis' : 'coût annexe';

  const handleClose = () => {
    if (pendingSaveCount > 0) {
      setShowCloseConfirm(true);
      return;
    }
    onClose();
  };

  const handleLinkCurrent = async (lineId: string) => {
    setLinkingLineId(lineId);
    await onLinkToCurrentChantier(lineId);
    setLinkingLineId(null);
  };

  const handleLinkOther = async (lineId: string, targetId: string, targetNom: string) => {
    setExpandedSelectorLineId(null);
    setLinkingLineId(lineId);
    await onLinkToOtherChantier(lineId, targetId, targetNom);
    setLinkingLineId(null);
  };

  const handleBatchLink = async () => {
    setBatchLinking(true);
    await onLinkAllToCurrentChantier(checkedIds);
    setBatchLinking(false);
  };

  // === Phase extraction ===
  if (isProcessing) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold tracking-tight">Scan en cours</h3>
          <span className="text-xs text-muted-foreground tabular-nums font-medium">
            {completedCount}/{totalCount}
          </span>
        </div>

        <div className="rounded-lg border border-border/50 bg-card/50 overflow-hidden divide-y divide-border/30">
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-3 px-3 py-2.5 text-sm">
              {f.status === 'done' && <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />}
              {f.status === 'extracting' && <Loader2 className="h-4 w-4 text-blue-400 animate-spin flex-shrink-0" />}
              {f.status === 'pending' && <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/25 flex-shrink-0" />}
              {f.status === 'error' && <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />}
              <span className={`truncate ${f.status === 'pending' ? 'text-muted-foreground/50' : ''}`}>
                {f.file.name}
              </span>
              <span className={`ml-auto text-[11px] flex-shrink-0 font-medium ${
                f.status === 'done' ? 'text-emerald-500' :
                f.status === 'extracting' ? 'text-blue-400' :
                f.status === 'error' ? 'text-red-400' :
                'text-muted-foreground/30'
              }`}>
                {f.status === 'done' && 'OK'}
                {f.status === 'extracting' && 'Analyse...'}
                {f.status === 'error' && 'Erreur'}
              </span>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <Progress value={progressPct} className="h-1.5" />
          <p className="text-[11px] text-muted-foreground text-right tabular-nums font-medium">{progressPct}%</p>
        </div>

        <Button variant="ghost" size="sm" onClick={onCancel} className="text-muted-foreground hover:text-foreground">
          Annuler le scan
        </Button>
      </div>
    );
  }

  // === Phase liaison ===
  return (
    <div className="space-y-4">
      {/* Header */}
      {allDone ? (
        <div className="flex items-center gap-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3.5 py-2.5">
          <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
          <span className="text-sm font-medium text-emerald-400">
            {savedCount} {docLabel}{savedCount > 1 ? 's' : ''} enregistré{savedCount > 1 ? 's' : ''}
          </span>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold tracking-tight">
            {completedCount} document{completedCount > 1 ? 's' : ''} scanné{completedCount > 1 ? 's' : ''}
          </h3>
          <span className="inline-flex items-center rounded-full bg-amber-500/15 text-amber-500 px-2.5 py-0.5 text-[11px] font-semibold tabular-nums">
            {pendingSaveCount} à lier
          </span>
        </div>
      )}

      {/* Liste des lignes — groupées par fichier parent */}
      <div className="space-y-2">
        {(() => {
          const groups: { parentFileId?: string; parentFileName?: string; lines: MultiOCRResultLine[] }[] = [];
          for (const line of allResultLines) {
            if (line.parentFileId) {
              const lastGroup = groups[groups.length - 1];
              if (lastGroup && lastGroup.parentFileId === line.parentFileId) {
                lastGroup.lines.push(line);
              } else {
                groups.push({ parentFileId: line.parentFileId, parentFileName: line.fileName, lines: [line] });
              }
            } else {
              groups.push({ lines: [line] });
            }
          }

          return groups.map((group, gIdx) => {
            // Groupe multi-ref
            if (group.parentFileId && group.lines.length > 0) {
              return (
                <div key={group.parentFileId} className="rounded-lg border border-blue-500/30 bg-card/30 overflow-hidden shadow-[0_0_8px_2px_rgba(245,158,11,0.25)]">
                  <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 border-b border-border/30">
                    <FileText className="h-3.5 w-3.5 text-muted-foreground/50 flex-shrink-0" />
                    <p className="text-xs font-medium text-muted-foreground truncate flex-1">
                      {group.parentFileName}
                    </p>
                    <span className="text-[10px] text-muted-foreground/50 bg-muted/50 rounded-full px-2 py-0.5 flex-shrink-0">
                      {group.lines.length} réf.
                    </span>
                  </div>
                  <div className="divide-y divide-border/20">
                    {group.lines.map((line) => (
                      <div key={line.id}>
                        <ResultLineRow
                          line={line}
                          chantierNom={chantierNom}
                          isLinking={linkingLineId === line.id}
                          isSubLine
                          restoredFromStore={restoredFromStore}
                          checked={checkedIds.has(line.id)}
                          onToggleCheck={() => toggleCheck(line.id)}
                          onLinkCurrent={() => handleLinkCurrent(line.id)}
                          onLinkOtherToggle={() => setExpandedSelectorLineId(
                            expandedSelectorLineId === line.id ? null : line.id
                          )}
                          onCreateChantier={() => setCreateChantierLineId(line.id)}
                          onRetry={() => onRetry(line.id)}
                          onDismiss={() => onDismiss(line.id)}
                        />
                        {expandedSelectorLineId === line.id && (
                          <div className="px-3 pb-2.5">
                            <ChantierSelector
                              entrepriseId={entrepriseId}
                              excludeChantierId={chantierId}
                              onSelect={(id, nom) => handleLinkOther(line.id, id, nom)}
                              onCancel={() => setExpandedSelectorLineId(null)}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            // Ligne simple
            const line = group.lines[0];
            return (
              <div key={line.id} className="rounded-lg border border-border/40 bg-card/20 overflow-hidden">
                <ResultLineRow
                  line={line}
                  chantierNom={chantierNom}
                  isLinking={linkingLineId === line.id}
                  isSubLine={false}
                  restoredFromStore={restoredFromStore}
                  checked={checkedIds.has(line.id)}
                  onToggleCheck={() => toggleCheck(line.id)}
                  onLinkCurrent={() => handleLinkCurrent(line.id)}
                  onLinkOtherToggle={() => setExpandedSelectorLineId(
                    expandedSelectorLineId === line.id ? null : line.id
                  )}
                  onCreateChantier={() => setCreateChantierLineId(line.id)}
                  onRetry={() => onRetry(line.id)}
                  onDismiss={() => onDismiss(line.id)}
                />
                {expandedSelectorLineId === line.id && (
                  <div className="px-3 pb-2.5 border-t border-border/20">
                    <ChantierSelector
                      entrepriseId={entrepriseId}
                      excludeChantierId={chantierId}
                      onSelect={(id, nom) => handleLinkOther(line.id, id, nom)}
                      onCancel={() => setExpandedSelectorLineId(null)}
                    />
                  </div>
                )}
              </div>
            );
          });
        })()}
      </div>

      {/* Bouton batch — brand amber */}
      {pendingSaveCount > 0 && (
        <Button
          onClick={handleBatchLink}
          disabled={batchLinking || checkedPendingCount === 0}
          className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold shadow-sm shadow-amber-500/20 transition-all disabled:opacity-40"
        >
          {batchLinking ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Liaison en cours...</>
          ) : (
            <>Lier la sélection à ce chantier ({checkedPendingCount})</>
          )}
        </Button>
      )}

      {/* Bouton fermer */}
      <Button variant="ghost" size="sm" onClick={handleClose} className="w-full text-muted-foreground hover:text-foreground">
        Fermer
      </Button>

      {/* Dialog création chantier */}
      <ChantierFormDialog
        open={!!createChantierLineId}
        onOpenChange={(open) => { if (!open) setCreateChantierLineId(null); }}
        entrepriseId={entrepriseId || ''}
        onSuccess={async (newChantierId) => {
          if (createChantierLineId && newChantierId) {
            setLinkingLineId(createChantierLineId);
            await onCreateAndLink(createChantierLineId, newChantierId);
            setLinkingLineId(null);
          }
          setCreateChantierLineId(null);
        }}
      />

      {/* AlertDialog confirmation fermeture */}
      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Des scans non enregistrés</AlertDialogTitle>
            <AlertDialogDescription>
              Vous avez {pendingSaveCount} document{pendingSaveCount > 1 ? 's' : ''} scanné{pendingSaveCount > 1 ? 's' : ''} non enregistré{pendingSaveCount > 1 ? 's' : ''}.
              Les résultats seront conservés et vous pourrez les retrouver en rouvrant l'onglet Scan IA.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Revenir aux résultats</AlertDialogCancel>
            <AlertDialogAction onClick={onClose}>
              Fermer quand même
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// === Sous-composant : ligne de résultat ===

function ResultLineRow({
  line,
  chantierNom,
  isLinking,
  isSubLine,
  restoredFromStore,
  checked,
  onToggleCheck,
  onLinkCurrent,
  onLinkOtherToggle,
  onCreateChantier,
  onRetry,
  onDismiss,
}: {
  line: MultiOCRResultLine;
  chantierNom?: string;
  isLinking: boolean;
  isSubLine: boolean;
  restoredFromStore: boolean;
  checked?: boolean;
  onToggleCheck?: () => void;
  onLinkCurrent: () => void;
  onLinkOtherToggle: () => void;
  onCreateChantier: () => void;
  onRetry: () => void;
  onDismiss: () => void;
}) {
  const montantHT = line.result?.totaux?.total_ht;
  const montantDisplay = line.montantTTC
    ? `${line.montantTTC.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € TTC`
    : montantHT
    ? `${montantHT.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € HT`
    : '';

  const label = isSubLine && line.reference ? line.reference : line.fileName;

  // Ligne sauvée
  if (line.status === 'saved') {
    return (
      <div className="flex items-center gap-2.5 text-sm px-3 py-2.5">
        <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
        <span className="truncate text-muted-foreground">{label}</span>
        {montantDisplay && (
          <span className="text-white/70 text-xs flex-shrink-0 tabular-nums">{montantDisplay}</span>
        )}
        <span className="text-emerald-500 text-xs ml-auto flex-shrink-0 font-medium truncate max-w-[140px]">
          → {line.linkedChantierNom}
        </span>
      </div>
    );
  }

  // Ligne erreur
  if (line.status === 'error') {
    return (
      <div className="flex items-center gap-2.5 text-sm px-3 py-2.5">
        <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
        <span className="truncate text-muted-foreground/50">{line.fileName}</span>
        <span className="text-red-400/70 text-[11px] font-medium flex-shrink-0">Illisible</span>
        <div className="flex gap-1.5 ml-auto flex-shrink-0">
          {!restoredFromStore && (
            <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-foreground" onClick={onRetry}>
              <RotateCcw className="h-3 w-3 mr-1" /> Réessayer
            </Button>
          )}
          <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-foreground" onClick={onDismiss}>
            Ignorer
          </Button>
        </div>
      </div>
    );
  }

  // Ligne OK — boutons d'action
  const hasDuplicate = !!line.duplicateWarning;

  return (
    <div>
      <div className="flex items-center gap-2.5 text-sm px-3 py-2.5">
        {onToggleCheck && (
          <Checkbox
            checked={checked}
            onCheckedChange={onToggleCheck}
            className="h-4 w-4 flex-shrink-0"
          />
        )}
        <div className={`h-2 w-2 rounded-full flex-shrink-0 ${hasDuplicate ? 'bg-amber-400' : 'bg-blue-400'}`} />
        <span className="truncate font-medium">{label}</span>
        {montantDisplay && (
          <span className="text-white/70 text-xs flex-shrink-0 tabular-nums">{montantDisplay}</span>
        )}

        {isLinking ? (
          <Loader2 className="h-4 w-4 text-blue-400 animate-spin ml-auto flex-shrink-0" />
        ) : (
          <div className="flex gap-1.5 ml-auto flex-shrink-0">
            <Button
              variant="default"
              size="sm"
              className="h-7 text-xs"
              onClick={onLinkCurrent}
              title={`Lier à ${chantierNom || 'ce chantier'}`}
            >
              <MapPin className="h-3 w-3 mr-1" />
              Ce chantier
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7 border-border/60 text-muted-foreground hover:text-blue-400 hover:border-blue-400/50 hover:bg-blue-400/10 transition-colors"
              onClick={onLinkOtherToggle}
              title="Lier à un autre chantier"
            >
              <LinkIcon className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7 border-border/60 text-muted-foreground hover:text-emerald-400 hover:border-emerald-400/50 hover:bg-emerald-400/10 transition-colors"
              onClick={onCreateChantier}
              title="Créer un chantier et lier"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
      {hasDuplicate && (
        <div className="flex items-center gap-1.5 px-3 pb-2 -mt-0.5 text-[11px] text-amber-500/80">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          <span>{line.duplicateWarning}</span>
        </div>
      )}
    </div>
  );
}
